/**
 * FreezeEventTypes — wire shape + thresholds for the always-on freeze sentinel
 * (2026-06-04). Recorded as `system`-domain DomainLog events (free JSONL +
 * history + attribute_at) — NO new buffer/bus/endpoint, just the message shape.
 *
 * Two physically distinct freeze classes, each unambiguous about its side:
 *
 *  - `client_freeze` — client frame production STOPPED (rAF gap > threshold).
 *    The whole UI locks up then jumps. This is ALWAYS on the local client side,
 *    but the rAF signal alone does not distinguish a JS main-thread block from
 *    renderer scheduling, compositor/GPU cadence, or host descheduling. A
 *    server/network stall does NOT cause this — the client keeps rendering,
 *    while only remote entities stop moving.
 *
 *  - `server_silence` — rAF is HEALTHY but no server state-patch has reached
 *    the client for > threshold. Remote entities are frozen while the UI is
 *    responsive. The server stalled or the transport (Cloudflare/WS) hiccuped.
 *    attribute_at then splits this further: coincident `server_tick_stall`
 *    ⇒ server event-loop; none ⇒ network/transport.
 *
 * The earlier single-signal `lastServerMsgAgeMs` discriminator was wrong: during
 * a client freeze the heartbeat can't update either, so it always inflated and
 * mislabeled client freezes as server. The split above fixes that by construction.
 */

export type FreezeKind = 'client_freeze' | 'server_silence';

/** One script entry from a Long Animation Frame, already reduced to primitives. */
export interface FreezeLoafScript {
  /** sourceFunctionName, else sourceURL, else invoker — whichever the browser gave. */
  name: string;
  /** e.g. 'user-callback', 'event-listener', 'resolve-promise', 'classic-script'. */
  invokerType: string;
  durationMs: number;
}

/**
 * Long Animation Frame attribution (2026-07-25). `longTasksMs` proved the main
 * thread was blocked — 141/162 focused freezes classified `js-longtask` — but a
 * longtask entry carries only a duration, so every phase counter read innocent
 * and the owner stayed anonymous. LoAF (Chromium 123+) names it: which script,
 * from which URL, invoked how, and how the frame split between script work and
 * style/layout/paint.
 *
 * Reading it:
 *  - big `scriptMs` with a named `scripts[0]` ⇒ our JS owns the stall, go there.
 *  - small `scriptMs` but big `renderMs` ⇒ the time is in style/layout/paint or
 *    the native GL/present path — a rendering-approach problem, not a JS one.
 *  - no entry at all covering the gap ⇒ the frame never ran; rAF was starved
 *    rather than blocked, which is a different defect.
 */
export interface FreezeLoafAttribution {
  /** Total frame duration reported by the browser (ms). */
  durationMs: number;
  /** Portion the browser counts as blocking (longtask-equivalent, ms). */
  blockingDurationMs: number;
  /** Summed script execution inside the frame (ms). */
  scriptMs: number;
  /** renderStart → frame end: style, layout, paint, commit (ms). */
  renderMs: number;
  /** styleAndLayoutStart → frame end (ms). Subset of renderMs. */
  styleAndLayoutMs: number;
  /** Longest-first, capped. Empty when the browser attributed no scripts. */
  scripts: FreezeLoafScript[];
}

/**
 * Self-attribution attached to a `client_freeze` so it explains ITSELF in the
 * domain log + attribute_at — no manual profiler/AllocCounters dance (2026-06-27).
 *
 *  - `topCounters` — the hottest instrumented call sites (AllocCounters) in the
 *    ~1s window before the freeze, per-second rate. Cross-browser. This is the
 *    signal that exposed the onChange listener leak (schema.onChange.npc 24,705/s).
 *  - `longTasksMs` — durations of any PerformanceObserver longtask overlapping
 *    the rAF gap (Chromium only; empty on Firefox/Safari).
 *  - `heapDeltaMB` — used-heap change across the gap (Chromium only; null else).
 *    A large negative delta means GC reclaimed near the stall and supports a
 *    GC suspicion; it does not prove the pause owned the full rAF gap.
 *  - `source` — coarse verdict derived from the above (heap-drop ⇒ gc-suspected,
 *    covering longtask ⇒ js-longtask, neither ⇒ unknown but topCounters still tell).
 */
export interface FreezeAttribution {
  topCounters: { key: string; perSec: number }[];
  /** Measured ms-of-work per second by frame phase, ranked descending. This is
   *  time ownership; `topCounters` is frequency. Prefer this for any decision. */
  phaseBudgetMs?: { phase: string; msPerSec: number }[];
  longTasksMs: number[];
  heapDeltaMB: number | null;
  /** Maximum setTimeout drift over the last ~2s (ms). Catches event-loop
   *  congestion from many short tasks / React Scheduler slices that the
   *  longtask observer (Chromium only) cannot see. */
  eventLoopLagMs: number;
  /** The Long Animation Frame overlapping the gap, when the browser reports one
   *  (Chromium 123+; absent on Firefox/Safari). See FreezeLoafAttribution. */
  loaf?: FreezeLoafAttribution;
  /**
   * `raf-throttled` and `presentation-stall` share the same evidence — frames
   * stopped while the event loop stayed healthy — and differ only by focus.
   * Unfocused is the browser deprioritising an occluded window (benign);
   * focused means frames are not reaching the screen while JS is idle, which is
   * what a compositor/GPU present hang looks like from inside the page.
   */
  source: 'js-longtask' | 'gc-suspected' | 'raf-throttled' | 'presentation-stall' | 'unknown';
}

/**
 * Which physical focus population a freeze belongs to. The 2026-07-22 incident
 * proved these are SEPARATE problems: `focused` gaps need root-cause ownership,
 * while `visible-unfocused` gaps are dominated by browser rAF throttling of a
 * backgrounded window and mainly need containment. Never merge them in a report.
 */
export type FreezeFocusClass = 'focused' | 'visible-unfocused' | 'hidden' | 'unknown';

export function classifyFocus(
  input: { documentHidden?: boolean; hasFocus?: boolean },
): FreezeFocusClass {
  const { documentHidden, hasFocus } = input;
  if (documentHidden === true) return 'hidden';
  if (documentHidden === undefined || hasFocus === undefined) return 'unknown';
  return hasFocus ? 'focused' : 'visible-unfocused';
}

/**
 * DomainLog event name for a sentinel trip that IS a freeze. Every consumer
 * that asks "did we freeze?" filters on this string.
 */
export const FREEZE_DOMAIN_LOG_EVENT = 'freeze_detected';

/**
 * DomainLog event name for a client rAF gap the browser produced ON PURPOSE by
 * throttling an unfocused window. Recorded, never counted as a freeze.
 */
export const THROTTLED_FRAMES_DOMAIN_LOG_EVENT = 'raf_throttled';

/**
 * Is this trip browser rAF throttling rather than a freeze?
 *
 * Chrome deprioritises `requestAnimationFrame` for a window that does not have
 * focus. The resulting gap trips the same rAF-gap detector a real freeze does,
 * so the sentinel cannot avoid seeing it — but it is the browser working
 * correctly, and it fires every single time the user alt-tabs away. Counting it
 * as a freeze put a permanent false-positive population in every session and
 * made every consumer responsible for remembering to filter on `focused`.
 *
 * Two deliberate narrowings:
 *
 *  - `server_silence` is NEVER throttling. Window focus does not throttle the
 *    SERVER; an unfocused client that stops receiving state patches has found a
 *    real server/transport stall and must stay a freeze.
 *  - `unknown` stays a freeze. It means we could not read focus (jsdom, a
 *    detached document, an older wire row), not that the window was unfocused.
 *    Reclassifying on absent evidence would silently swallow real freezes in
 *    exactly the environments least able to notice.
 */
export function isThrottledFrameGap(
  input: { kind: FreezeKind; focusClass?: FreezeFocusClass },
): boolean {
  if (input.kind !== 'client_freeze') return false;
  return input.focusClass === 'visible-unfocused' || input.focusClass === 'hidden';
}

/**
 * The DomainLog event name a sentinel trip should be recorded under. This is
 * the single point that keeps `raf_throttled` out of every freeze query by
 * DEFAULT — `/api/diag/domain-log?event=freeze_detected`, computeFreezeVerdict,
 * attribute-at, session_forensics, and every QA round already filter on the
 * freeze name, so none of them has to learn a new rule.
 */
export function freezeDomainLogEvent(
  input: { kind: FreezeKind; focusClass?: FreezeFocusClass },
): string {
  return isThrottledFrameGap(input)
    ? THROTTLED_FRAMES_DOMAIN_LOG_EVENT
    : FREEZE_DOMAIN_LOG_EVENT;
}

export type FreezeDeliveryPath = 'live' | 'pre_room_replay' | 'send_retry';

/** Client-side delivery provenance for the existing DomainLog lane. */
export interface FreezeDeliveryMetadata {
  /** Direct send, boot-time replay after room bind, or retry after send threw. */
  path: FreezeDeliveryPath;
  /** When the client retained the row; null for a direct live send. */
  bufferedAt: number | null;
  /** When `room.send('netobs:freezeEvent')` was invoked. */
  sentAt: number;
}

/** Sent client→server as `netobs:freezeEvent` when the sentinel trips. */
export interface FreezeEventWire {
  /** Page-unique diagnostic id used to suppress client-side replay duplicates. */
  clientEventId?: string;
  /** client Date.now() at detection. */
  capturedAt: number;
  /** which physical class of freeze (see above). */
  kind: FreezeKind;
  /** client_freeze: the rAF gap, ms. server_silence: the silence duration, ms. */
  stallMs: number;
  /** route the user was on (location.pathname), for context. */
  route: string;
  /** used JS heap MB at trip time, when the browser exposes it; else null. */
  heapUsedMB: number | null;
  /** self-attribution — present on client_freeze only (see FreezeAttribution). */
  attribution?: FreezeAttribution;
  /** Populated by the delivery owner immediately before the server send. */
  delivery?: FreezeDeliveryMetadata;
  /** Current level/map id at trip time (from MapSystem). */
  levelId?: string;
  /** Browser tab visibility at trip time — helps disambiguate background-tab
   *  rAF pauses from real freezes. */
  documentHidden?: boolean;
  visibilityState?: 'hidden' | 'visible' | 'prerender' | 'unloaded';
  /** Whether the document had focus at trip time. */
  hasFocus?: boolean;
  /** Derived focus population (see FreezeFocusClass). Persisted so DomainLog
   *  queries and probes can split the two classes without re-deriving. */
  focusClass?: FreezeFocusClass;
  /** Time the last GameHost scene tick took (ms). */
  sceneTickMs?: number;
  /** Active atmosphere preset + layer kinds for scene-FX diagnosis. */
  atmosphere?: {
    presetId: string | null;
    layerKinds: string[];
    spriteCount: number;
    lightCount: number;
  };
  /** Renderer / ticker state at the time of the stall. */
  renderer?: {
    screenWidth: number;
    screenHeight: number;
    resolution: number;
    tickListenerCount: number;
    activeParticleCount: number;
    activeEmitterCount: number;
    lastRenderMs: number;
    lastTickerMs: number;
    gpuBackend: string;
    gpuRenderer: string;
    glowChildCount: number;
    vfxChildCount: number;
    worldChildCount: number;
  };
  /** Selected cumulative counters at trip time (from AllocCounters). */
  runtime?: Record<string, number>;
  /** Last direct server-activity receipt at a server_silence trip. This keeps
   * the first missing seam durable in DomainLog/attribute-at after the browser
   * closes; the bounded receipt timeline remains in NetworkTracer/run.json. */
  serverActivity?: {
    bindingGeneration: number;
    lastSource: ServerActivitySource | null;
    heartbeatReceipts: number;
    statePatchReceipts: number;
    lastHeartbeatSequence: number | null;
    lastServerSequence: number | null;
    lastHeartbeatTraceId: string | null;
    lastRoomInstanceId: string | null;
    lastHeartbeatServerSentAt: number | null;
    lastHeartbeatClientReceivedAt: number | null;
    lastRawFrameSequence: number | null;
    lastRawFrameReceivedAt: number | null;
    lastRawToCallbackMs: number | null;
    lastHeartbeatGapMs: number | null;
  };
}

/** Dedicated server liveness pulse. `sequence` is scoped to one GameRoomTick
 * instance and exists only to correlate server emission with client receipt;
 * it is not gameplay state or an ordering authority. */
export interface ServerHeartbeatWire {
  /** Server wall-clock timestamp when GameRoomTick decided the pulse was due. */
  t: number;
  /** Monotonic per-room heartbeat sequence, starting at 1. */
  sequence: number;
  /** Added by the server NetworkDiagnostics raw-broadcast closer. */
  __netTrace?: {
    traceId?: string;
    serverSeq?: number;
    roomId?: string;
    roomInstanceId?: string;
    serverSentAt?: number;
  };
}

export type ServerActivitySource = 'room_bind' | 'state_patch' | 'heartbeat';

/** One bounded client receipt row used by netcode rounds and attribute-at
 * analysis. Wall-clock fields correlate with server logs; monotonic fields
 * drive the actual silence doctrine and never mix clock domains. */
export interface ServerHeartbeatReceipt {
  roomId: string | null;
  sessionId: string | null;
  sequence: number | null;
  traceId: string | null;
  serverSequence: number | null;
  roomInstanceId: string | null;
  serverSentAt: number | null;
  clientReceivedAt: number;
  clientReceiptMonotonicMs: number;
  /** Sequence/timestamp at the Colyseus transport.events.onmessage entry. This
   * wrapper runs before Room decoding and is restored on room replacement. */
  rawFrameSequence: number | null;
  rawFrameReceivedAt: number | null;
  rawToCallbackMs: number | null;
  gapMs: number | null;
}

export interface ServerActivityDiagnostics {
  capturedAt: number;
  roomId: string | null;
  sessionId: string | null;
  bindingGeneration: number;
  lastSource: ServerActivitySource | null;
  lastActivityAt: number | null;
  lastActivityMonotonicMs: number | null;
  heartbeatReceipts: number;
  statePatchReceipts: number;
  silenceEpisodes: number;
  silenceRearms: number;
  silenceArmed: boolean;
  recentHeartbeats: ServerHeartbeatReceipt[];
}

export const FREEZE_CONFIG = {
  /** rAF gap (ms) above which a frame counts as a client render-loop freeze
   *  (~9 frames @60Hz). Raise to cut noise, lower to catch micro-hitches. */
  CLIENT_FREEZE_THRESHOLD_MS: 150,
  /** ms of no inbound server signal (state patch OR 1Hz heartbeat) while rAF is
   *  healthy, above which the server is "silent". The dedicated server heartbeat
   *  (1Hz) is the floor, so 2s clears idle false-positives (no schema change ≠
   *  silent) while still catching a real stall. */
  SERVER_SILENCE_THRESHOLD_MS: 2000,
  /** server inter-tick delta (ms) above which the loop is considered stalled.
   *  Nominal tick is ~14–22ms; 120ms ⇒ ~6–8 missed fires, clearly a real stall
   *  (50ms grazed benign GC/jitter). */
  SERVER_DRIFT_THRESHOLD_MS: 120,
  /** rAF gaps above this are debugger-pause / OS-sleep / hidden-tab-throttle
   *  artifacts, not felt in-game freezes — skip them (no portable debugger detect). */
  DEBUGGER_PAUSE_CEILING_MS: 5000,
  /** interval of the server liveness heartbeat broadcast, ms. */
  SERVER_HEARTBEAT_INTERVAL_MS: 1000,
  /** min gap between recorded events per kind, ms (flood guard). */
  MIN_LOG_INTERVAL_MS: 1000,
} as const;
